<template>
  <div class="wrapper">
    <admin-title :title="$route.meta.name"></admin-title>
    <tp-table
      :table-data="list"
      :columns="columns"
      :show-pagination="false"
    />
    <capital-org-detail
      :visible.sync="detailDialog.visible"
      :id="detailDialog.id"
      :mode="detailDialog.mode"
      @refreshList="getList"
    />
    <support-combo-dialog
      :visible.sync="supportComboDialog.visible"
      :id="supportComboDialog.id"
      :support-combo-id-list="supportComboDialog.supportComboIdList"
      @refreshList="getList"
    />
  </div>
</template>

<script>
import { authBtnMixin } from "@/mixins/authBtnMixin";
import { getCapitalOrgList, getCapitalOrgDetail, editPushOrderStatus } from "./api.js";
import CapitalOrgDetail from "./detail.vue";
import SupportComboDialog from "./support-combo-dialog.vue";

// 机构资方不分页，列配置参考 views/channel/channel-list/list.vue
const columns = [
  {
    label: "机构ID",
    prop: "id",
    width: "100",
  },
  {
    label: "机构名称",
    prop: "orgName",
    minWidth: "180",
  },
  {
    label: "推单状态",
    prop: "pushOrderStatus",
    width: "110",
    customRender(h, row) {
      return (
        <el-switch
          value={!!row["pushOrderStatus"]}
          disabled={!this.isShowBtn(this.AUTH_BTN.capital_org_push_order_status)}
          onChange={(val) => this.onPushOrderStatusChange(row, val)}
        />
      );
    },
  },
  {
    label: "当日推单上限",
    width: "180",
    customRender(h, row) {
      return (
        <div class="push-order-progress">
          <div class="nums">
            <span class="today">{row["todayPushOrderNum"]}</span>
            <span class="separator">/</span>
            <span class="max">{row["dailyMaxPushOrderNum"]}</span>
          </div>
          <el-progress
            percentage={this.calcPushOrderPercentage(row)}
            show-text={false}
            stroke-width={6}
          />
        </div>
      );
    },
  },
  {
    label: "推单比重",
    prop: "pushOrderRate",
    width: "100",
    customRender(h, row) {
      return <span>{row["pushOrderRate"]}%</span>;
    },
  },
  {
    label: "融资比例",
    prop: "investRate",
    width: "100",
    customRender(h, row) {
      return <span>{row["investRate"]}%</span>;
    },
  },
  {
    label: "投资IRR",
    prop: "investIrr",
    width: "100",
    customRender(h, row) {
      return <span>{row["investIrr"]}%</span>;
    },
  },
  {
    label: "关联方案数",
    width: "110",
    customRender(h, row) {
      const num = Number(row["supportComboNum"]);
      return (
        <el-tag type="success" size="small" class="support-combo-tag">
          {num === 0 ? "全方案" : row["supportComboNum"]}
        </el-tag>
      );
    },
  },
  {
    label: "清分方式",
    prop: "royaltyMethodName",
    minWidth: "120",
  },
  {
    label: "操作",
    minWidth: "150",
    fixed: "right",
    customRender(h, row) {
      // 推单状态开启时，禁止编辑基础信息和关联方案
      const isPushing = !!row["pushOrderStatus"];
      return (
        <div class="operations">
          <el-link
            underline={false}
            type="primary"
            disabled={isPushing || !this.isShowBtn(this.AUTH_BTN.capital_org_edit)}
            onClick={() => this.openDialog("edit", row["id"])}
          >编辑</el-link>
          <el-link
            underline={false}
            type="primary"
            disabled={isPushing || !this.isShowBtn(this.AUTH_BTN.capital_org_support_combo)}
            onClick={() => this.openSupportComboDialog(row)}
          >关联方案</el-link>
        </div>
      );
    },
  },
];

export default {
  name: "CapitalOrgList",
  mixins: [authBtnMixin],
  components: { CapitalOrgDetail, SupportComboDialog },
  provide() {
    return { context: this };
  },
  data() {
    return {
      // 机构资方不分页，接口直接返回完整数组
      list: [],
      columns,
      detailDialog: {
        visible: false,
        id: null,
        mode: "edit",
      },
      supportComboDialog: {
        visible: false,
        id: null,
        supportComboIdList: [],
      },
    };
  },
  mounted() {
    this.getList();
  },
  methods: {
    // 当日推单进度 = todayPushOrderNum / dailyMaxPushOrderNum，clamp 到 [0, 100]
    calcPushOrderPercentage(row) {
      const max = Number(row.dailyMaxPushOrderNum) || 0;
      if (max <= 0) return 0;
      const today = Number(row.todayPushOrderNum) || 0;
      const ratio = (today / max) * 100;
      return Math.min(100, Math.round(ratio * 100) / 100);
    },
    async getList() {
      const res = await getCapitalOrgList();
      this.list = Array.isArray(res) ? res : [];
    },
    async onPushOrderStatusChange(row, val) {
      const oldVal = row.pushOrderStatus;
      row.pushOrderStatus = val;
      try {
        await editPushOrderStatus({ id: row.id, pushOrderStatus: val });
        this.$message.success("操作成功");
      } catch (e) {
        // 还原开关状态
        row.pushOrderStatus = oldVal;
      }
    },
    openDialog(mode, id) {
      this.detailDialog = { visible: true, id, mode };
    },
    async openSupportComboDialog(row) {
      try {
        const res = await getCapitalOrgDetail({ id: row.id });
        this.supportComboDialog = {
          visible: true,
          id: row.id,
          supportComboIdList: Array.isArray(res.supportComboIdList)
            ? res.supportComboIdList
            : [],
        };
      } catch (e) {
        this.supportComboDialog = {
          visible: true,
          id: row.id,
          supportComboIdList: [],
        };
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.wrapper /deep/ {
  width: 100%;

  .operations {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;
  }

  // 关联方案数：圆角 tag
  .support-combo-tag {
    border-radius: 12px;
  }

  .push-order-progress {
    .nums {
      display: flex;
      align-items: center;
      font-size: 12px;
      line-height: 1.2;
      margin-bottom: 4px;
      color: #606266;
      .today {
        color: #409eff;
        font-weight: 600;
      }
      .separator {
        margin: 0 4px;
        color: #c0c4cc;
      }
      .max {
        color: #606266;
      }
    }
    .el-progress {
      margin-bottom: 0;
    }
  }
}
</style>
